import React, { useId, useMemo } from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import ProgressiveHeroImage from "../ProgressiveHeroImage";
import {
  SlotRenderer,
  EmptyMediaSlotAffordance,
  SlotCapacityAffordance,
  EditableSlotMediaOverlay,
  getHeroImageStyle,
  useHeroAnimation,
  useHeroVerticalPadding,
} from "./heroModeUtils";
import "./heroModeAnimations.css";
import "./heroCircleSplit.css";

/**
 * HeroCircleSplit — the Circle Split family renderer.
 *
 * Sister renderer to HeroDiagonalSplit. Same half-image / half-text
 * composition, but the seam between the photo half and the text half
 * is a *curve* instead of a straight tilted line — the photo reads
 * as a slice of a big circle bulging gently (canonical) or
 * dramatically (deep) into the text area.
 *
 * The arc depth and per-image curve coordinates depend on the mode
 * id (canonical "subtle inward arc" vs. "deep near-semicircular
 * bulge") and the number of images slotted into ``gallery``
 * (1 image → photo on the LEFT only, text on the right; 2 images →
 * photos flank the text on both sides with mirrored bulges pinching
 * the text into a tapered middle column).
 *
 * Geometry overview
 * -----------------
 * Diagonal Split uses ``clip-path: polygon(...)`` because every
 * boundary is a straight line — a polygon's vertex list IS the
 * shape. Curves can't be expressed as a polygon vertex list, so
 * Circle Split uses a different mask system:
 *
 *   * Each photo wrapper carries ``clip-path: url(#<unique-id>)``
 *     pointing at an SVG ``<clipPath>`` definition rendered
 *     inline inside the same hero root.
 *   * The ``<clipPath>`` uses ``clipPathUnits="objectBoundingBox"``
 *     so its coordinate system is normalised 0–1 of the photo
 *     wrapper's bounding box. The clip therefore scales with the
 *     wrapper at every breakpoint without a re-compute (responsive
 *     by construction).
 *   * The clipped shape is a single ``<path>`` whose only
 *     non-straight segment is a cubic Bezier curve (``C cx1,cy1
 *     cx2,cy2 x,y``) on the inner edge. The Bezier control points
 *     are the only thing that varies between the subtle and deep
 *     siblings.
 *
 * Why ``<clipPath>`` referenced via ``clip-path: url(#...)`` and
 * not ``clip-path: path("...")``:
 *   * ``clip-path: path()`` uses absolute-pixel coordinates by
 *     default — it has no equivalent to ``clipPathUnits=
 *     "objectBoundingBox"``. We'd have to re-compute the path
 *     string on every layout change to keep the curve in sync
 *     with the wrapper size. The ``<clipPath>`` route gets
 *     responsive scaling for free.
 *   * ``<clipPath>`` definitions can carry multiple ``<path>``
 *     elements, opening the door to compound shapes (e.g. a
 *     curve plus a notch) without re-engineering the mask
 *     system. Future siblings of this family can lean on that.
 *
 *   * ``circle_split`` (canonical, gentle inward bulge)
 *
 *     Single image:                  Two images:
 *
 *       ┌──⌒─────────────┐            ┌──⌒          ⌒──┐
 *       │ph │   text     │            │ph │   text  │ ph│
 *       │ ot⟩             │            │ ot⟩          ⟨oto
 *       │ o │   text     │            │ o │   text  │ ph│
 *       └──⌒─────────────┘            └──⌒          ⌒──┘
 *
 *     The photo regions are deliberately the MINORITY of the
 *     hero (~30% of width on average each in the single-image
 *     case, ~22% in the two-image case). The bulge is shallow
 *     — enough to read as a curve, not enough to crowd the
 *     text column. Earlier values that made the photo take
 *     ~half the hero on average squeezed the text column at
 *     the bulge midpoint; the present geometry trades some
 *     "photo dominates" punchiness for guaranteed text
 *     legibility on every breakpoint.
 *
 *   * ``circle_split_deep`` (deep near-semicircular bulge)
 *
 *     Single image:                  Two images:
 *
 *       ┌─╮             ─┐            ┌─╮            ╭─┐
 *       │P ⟩    text     │            │ P⟩  text   ⟨ P│
 *       │  ⟩             │            │  ⟩          ⟨  │
 *       │P ⟩    text     │            │ P⟩  text   ⟨ P│
 *       └─╯             ─┘            └─╯            ╰─┘
 *
 *     The bulge is deep ENOUGH (depth ratio ~2.75) that the
 *     photo reads as a disc segment anchored just off-canvas
 *     — but the absolute extent is still the minority of the
 *     hero (~55% peak for single-image, ~38% peak for
 *     two-image). The two-image variant deliberately stops
 *     the bulge well clear of x=0.5 so the text column at
 *     the vertical mid-pinch has a comfortable band instead
 *     of the near-collapse the earlier ``CTRL_X = 0.50``
 *     produced.
 *
 * Why this lives in one renderer (not two)
 * ----------------------------------------
 * Same single-renderer-per-family rationale ``HeroDiagonalSplit``
 * uses: the two siblings differ only in (a) the cubic Bezier
 * control points on the inner edge of each photo wrapper and
 * (b) the text-column inset (deeper bulge needs more padding
 * to stay clear of the photo). Everything else (slot rendering,
 * empty-state affordance, mobile fallback, two-image symmetric
 * mirroring) is shared.
 */

// ---------------------------------------------------------------------------
// Mode-id constants
// ---------------------------------------------------------------------------
//
// Centralised so the dispatch on ``item.hero_mode`` reads from a
// single source. A future addition (e.g. ``circle_split_offset``
// for a vertically-shifted disc anchor) lands in this constant
// block first before touching the path factory.

const MODE_CIRCLE_SUBTLE = "circle_split";
const MODE_CIRCLE_DEEP = "circle_split_deep";
// Organic-seam sibling — the inner edge is a CHAIN of small
// cubic Bezier segments with deliberately offset control points
// so the line reads as torn-paper / hand-drawn rather than a
// geometric arc. Same single-renderer-per-family rule the
// subtle/deep pair use: only the path factory differs, all
// other dispatch / layout / mobile fallback / text inset logic
// is shared (the inset uses the max horizontal extent of the
// wobbly path instead of a single bulge ``ctrlX`` value).
const MODE_CIRCLE_ORGANIC = "circle_split_organic";

// Defensive cap mirroring ``CircleSplitMode._CIRCLE_SPLIT_MAX_IMAGES``
// in the backend definition. Past 2 images the curves would
// overlap each other (especially for the deep sibling) and the
// disc-segment reading collapses into noise. The cap protects
// the family's "1 disc" or "2 discs framing the text" identity
// even if the editor allowed extra items by mistake.
const MAX_IMAGES = 2;

// ---------------------------------------------------------------------------
// Per-breakpoint tuning
// ---------------------------------------------------------------------------
//
// Same shape as HeroDiagonalSplit's ``BP_CONFIG`` so a future
// shared base abstraction (if the family pair grows further)
// can lift these tables into a common helper. Values:
//
//   ``contentPx`` / ``contentPy`` — outer padding for the text
//     column so heading / body / CTA never butt against the
//     visible hero edge. Mobile uses tighter values because the
//     hero shrinks.
//   ``stackedTrayHeight`` — height of each circular photo tray when
//     the layout collapses to stacked (mobile). Picked so the photo
//     reads as a meaningful visual block instead of a shallow banner.
//   ``isStacked`` — controls whether the photos render as a
//     curve-clipped overlay (desktop) or as a stacked
//     rectangular block above the text (mobile). The curved cut
//     only reads as a deliberate visual statement when the hero
//     is wide enough for the bulge to have room; on phones the
//     bulge would either consume most of the text column or
//     register as visual noise.
const BP_CONFIG = {
  extra_large: { contentPx: 80, contentPy: 64, isStacked: false, stackedTrayHeight: 320 },
  large:       { contentPx: 64, contentPy: 56, isStacked: false, stackedTrayHeight: 300 },
  medium:      { contentPx: 48, contentPy: 48, isStacked: false, stackedTrayHeight: 280 },
  small:       { contentPx: 24, contentPy: 32, isStacked: true,  stackedTrayHeight: 320 },
  extra_small: { contentPx: 16, contentPy: 24, isStacked: true,  stackedTrayHeight: 280 },
};

// ---------------------------------------------------------------------------
// Curve geometry
// ---------------------------------------------------------------------------
//
// Each ``<clipPath>`` contains a single ``<path>`` whose ``d``
// attribute encodes the photo's visible region as four segments:
//
//   1. Move to the photo's outer-anchor top corner (M).
//   2. Line along the top edge to where the curve starts.
//   3. Cubic Bezier curve down the inner edge to where the curve
//      ends along the bottom edge.
//   4. Line back along the bottom edge to the outer-anchor
//      bottom corner, then close the path (Z).
//
// The Bezier control points (cx1, cy1, cx2, cy2) are the ONLY
// thing that varies between the subtle and deep siblings. By
// construction the top and bottom anchor points sit at the same
// horizontal x — that keeps the photo's silhouette symmetric
// vertically (the bulge is at y=0.5 by construction; tuning
// ``cy1`` / ``cy2`` would skew the bulge upward or downward).
//
// All coordinates are in objectBoundingBox space (0–1 of the
// wrapper's bounding box) so the clip scales with the wrapper at
// every breakpoint without re-computation.

// Subtle (canonical) — gentle inward bulge.
//
//   * Single image: top/bottom anchor at x = 0.30 (photo takes
//     ~30% of the hero by anchor); bulge max at x ≈ 0.40 (the
//     curve reaches about 10% past the anchor at vertical
//     centre — enough to read as a curve without crowding the
//     text column).
//   * Two images: top/bottom anchor at x = 0.20 (each photo
//     takes ~20% of the hero by anchor); bulge max at x ≈ 0.28
//     (each photo bulges 8% inward, leaving a generous text
//     column wide enough to seat a heading even at the bulges'
//     mid-line pinch).
//
// The values are deliberately well under 50% so the photo
// occupies the MINORITY of the hero — same rationale Diagonal
// Split's ``SUBTLE_BASE_PCT = 30`` uses (an earlier draft made
// the photo take ~half the hero on average and crowded the
// text column out of legibility on every breakpoint).
const SUBTLE_SINGLE_TOP_X = 0.30;
const SUBTLE_SINGLE_CTRL_X = 0.40;
const SUBTLE_TWO_TOP_X = 0.20;
const SUBTLE_TWO_CTRL_X = 0.28;

// Deep (loud) — near-semicircular bulge. The photo reads as
// literally being a disc segment.
//
//   * Single image: top/bottom anchor at x = 0.20; bulge max
//     at x ≈ 0.55 (the disc looks like a half-disc tucked
//     against the left edge — substantially taller than wide
//     because it's anchored at 20% but bulges to 55%).
//   * Two images: top/bottom anchor at x = 0.15; bulge max at
//     x ≈ 0.38 (each photo's bulge stays well clear of the
//     centre line, leaving a comfortable text band at the
//     vertical mid-pinch instead of the near-collapse the
//     earlier ``CTRL_X = 0.50`` produced).
//
// The deep sibling is still LOUDER than the subtle sibling
// (the bulge depth ratio ``ctrl/top`` is ~2.75 here vs ~1.4
// for subtle), preserving the family's "subtle vs. deep"
// identity contrast — the absolute extents are just smaller
// in both directions so neither sibling crowds the text out.
const DEEP_SINGLE_TOP_X = 0.20;
const DEEP_SINGLE_CTRL_X = 0.55;
const DEEP_TWO_TOP_X = 0.15;
const DEEP_TWO_CTRL_X = 0.38;

// Build the SVG path ``d`` attribute for a LEFT-anchored photo,
// expressed in coordinates LOCAL to the wrapper's bounding box
// (0–1 of the bbox, not of the hero).
//
// The photo's outer edge is the wrapper's left edge (x=0); its
// inner edge is the cubic Bezier curve. ``topX`` and ``ctrlX``
// are the anchor / bulge x values in HERO 0–1 coordinates;
// ``bboxMaxX`` is the wrapper's hero-width fraction (= ``ctrlX``
// for the smooth siblings, since by construction the curve
// reaches its extreme exactly at ``ctrlX``). To re-express in
// wrapper-local coordinates we divide all x values by
// ``bboxMaxX`` so the curve hits ``x_local = 1`` at its extreme.
//
// Path components (local coords):
//   M 0,0           — start at top-left corner of bbox (= hero left edge)
//   L topL,0        — line right along the top edge to the bezier start
//                     where ``topL = topX / bboxMaxX``
//   C 1,0   1,1   topL,1
//                   — cubic bezier from (topL,0) to (topL,1) bulging
//                     out to local x=1 at the vertical midpoint
//                     (bbox right edge = curve extreme by construction).
//   L 0,1           — line back along the bottom edge to the
//                     bottom-left corner.
//   Z               — close the path.
//
// This re-expression is purely a coordinate change — the visible
// curve shape is identical to the earlier ``polygon-style``
// hero-coord path. The benefit is that the wrapper can now be
// sized exactly to ``bboxMaxX`` of the hero (instead of
// full-bleed) so ``object-fit: cover`` on the inner ``<img>``
// centres the image content on the visible region, not on the
// hero — see the bbox-sizing rationale in the refactored
// ``.hero-circle-split-photo`` CSS rule.
function buildLeftLocalPath(topX, bboxMaxX) {
  const topL = topX / bboxMaxX;
  return `M 0,0 L ${topL},0 C 1,0 1,1 ${topL},1 L 0,1 Z`;
}

// Mirror of ``buildLeftLocalPath`` — for a RIGHT-anchored
// photo. The wrapper sits at the right side of the hero
// (left = ``(1 - bboxMaxX) * 100%``), and the path is the
// LEFT path mirrored across the wrapper's vertical centre so
// the outer edge (x=1 in local coords) coincides with the
// hero's right edge.
function buildRightLocalPath(topX, bboxMaxX) {
  const topL = topX / bboxMaxX;
  // Mirror across local x=0.5: every x → 1-x. Outer edge
  // (was at 0) is now at 1; inner anchor (was at topL) is
  // now at 1-topL; bulge extreme (was at 1) is now at 0.
  return `M 1,0 L ${1 - topL},0 C 0,0 0,1 ${1 - topL},1 L 1,1 Z`;
}

// ---------------------------------------------------------------------------
// Organic (irregular continuous) seam geometry
// ---------------------------------------------------------------------------
//
// Where the subtle / deep siblings express the inner edge as a
// SINGLE cubic Bezier (a clean disc segment), the organic
// sibling expresses it as a CHAIN of multiple smaller cubic
// Beziers with deliberately offset control points so the line
// reads as torn-paper / hand-drawn rather than templated.
//
// Each spec describes one organic LEFT-anchored seam:
//   * ``topX`` / ``bottomX`` — the inner-edge anchor x at the
//     top and bottom edges of the bounding box. Kept close
//     (but not identical) so the seam doesn't look perfectly
//     symmetric vertically — a small top↔bottom delta is
//     part of the hand-drawn identity.
//   * ``segments`` — ordered list of cubic-bezier descriptors,
//     one per wave segment travelling from the previous anchor
//     down to ``end``. Each segment carries its own ``cp1`` /
//     ``cp2`` so the wobble in/out of the average anchor x is
//     fully under designer control; alternating cp x values
//     above/below the anchor is what produces the wave.
//
// The chain segments cover y ∈ [0, 1] continuously (the last
// segment's ``end[1]`` should be 1.0), and the whole spec
// drives both the LEFT (raw) and RIGHT (mirrored across x=0.5)
// emitters below — same wave shape mirrored, same family
// identity with two-image symmetry.
//
// Why structured spec + emitter, not a hand-written path
// string per side: the RIGHT-anchored seam needs every x
// flipped to ``1 - x`` while every y / segment ordering /
// control-point pairing stays identical. Parsing a path
// string to flip just the x coordinates is fragile (mixing
// up x and y mid-string would silently produce a broken
// curve). Driving both emitters from the same structured
// data keeps the LEFT/RIGHT mirror correct by construction
// and makes future spec tweaks (deeper wobble, more
// segments) a single-source change.

// Single-image organic seam.
//
// Average anchor x ≈ 0.30 (matching the subtle sibling's
// single-image anchor of 0.30), with the wobble producing
// bulges ranging from ``x ≈ 0.18`` (deepest dent inward of
// average) to ``x ≈ 0.42`` (deepest bulge outward into the
// text). Four wave segments — enough to read as multiple
// wobbles down the seam, few enough that the line stays
// graceful instead of devolving into noise. Scaled down from
// the earlier 0.50-anchor / 0.72-bulge values for the same
// "image takes the minority of the hero" reason the smooth
// siblings now use much smaller extents.
const ORGANIC_SINGLE_SPEC = {
  topX: 0.30,
  bottomX: 0.34,
  segments: [
    { cp1: [0.40, 0.05], cp2: [0.37, 0.18], end: [0.29, 0.22] },
    { cp1: [0.22, 0.27], cp2: [0.24, 0.42], end: [0.34, 0.48] },
    { cp1: [0.42, 0.54], cp2: [0.37, 0.68], end: [0.28, 0.72] },
    { cp1: [0.18, 0.76], cp2: [0.25, 0.92], end: [0.34, 1.00] },
  ],
};

// Two-image organic seam (left photo). Average anchor x ≈ 0.22
// matching the subtle two-image anchor (0.20), with wobble
// bounded so the bulge max (``x ≈ 0.32``) sits well clear of
// x=0.5 — the mirrored right-photo seam will bulge from the
// opposite side up to ``x = 1 - 0.32 = 0.68`` minimum,
// leaving a generous centred text column with matching
// irregular dents on both sides. Four segments same as
// single-image to keep family-internal rhythm consistent.
const ORGANIC_TWO_SPEC = {
  topX: 0.22,
  bottomX: 0.25,
  segments: [
    { cp1: [0.30, 0.05], cp2: [0.28, 0.18], end: [0.22, 0.22] },
    { cp1: [0.16, 0.27], cp2: [0.18, 0.42], end: [0.25, 0.48] },
    { cp1: [0.32, 0.54], cp2: [0.28, 0.68], end: [0.21, 0.72] },
    { cp1: [0.14, 0.76], cp2: [0.20, 0.92], end: [0.25, 1.00] },
  ],
};

// Conservative upper bound for the seam's horizontal reach,
// used by the text-inset calculator. We take the max over all
// anchor and control x values: by the cubic Bezier convex-hull
// property, the curve never exits the convex hull of its
// control polygon, so the actual bulge max is ≤ this value.
// Safe to use as the inset reservation — it can over-reserve
// by a few percent, but never under-reserves (the text never
// crosses the visible curve).
function organicMaxX(spec) {
  let m = Math.max(spec.topX, spec.bottomX);
  for (const seg of spec.segments) {
    m = Math.max(m, seg.cp1[0], seg.cp2[0], seg.end[0]);
  }
  return m;
}

// Exact horizontal reach of the smooth siblings' bezier in HERO
// coordinates. The cubic from (topL, 0) → (1, 0) → (1, 1) →
// (topL, 1) is symmetric in y about 0.5 and hits its maximum x
// at t = 0.5 (vertical tangent). At that point:
//
//   x_local(0.5) = 0.125·topL + 3·0.25·0.5·1 + 3·0.5·0.25·1
//                + 0.125·topL
//                = 0.25·topL + 0.75
//
// where topL = topX / bboxMaxX. Rescaling by ``bboxMaxX`` to get
// hero coords:
//
//   x_hero(0.5) = (0.25·topL + 0.75)·bboxMaxX
//               = 0.25·topX + 0.75·bboxMaxX
//
// Notice this is STRICTLY less than ``bboxMaxX`` (because
// ``topX < bboxMaxX``) — the wrapper's right edge sits past
// where the visible curve actually reaches. Earlier text-inset
// math used ``bboxMaxX`` directly and pushed the text column
// past that edge, leaving a visible empty band between the
// photo's curved edge and the text. Using the exact curve max
// instead lets the text sit immediately next to the visible
// curve (at its widest extent) with only ``contentPx`` of
// breathing room — the "text wedged next to the photo" reading
// the family's editorial layout aims for.
//
// SAFETY: this is the EXACT bulge max for the smooth sibling
// bezier shape — there's no over-reservation needed because the
// curve genuinely never exceeds this x. The inset always
// includes an additional ``contentPx`` cosmetic gap.
function smoothCurveMaxX(topX, bboxMaxX) {
  return 0.25 * topX + 0.75 * bboxMaxX;
}

// Emit the SVG path ``d`` attribute for the LEFT-anchored
// organic seam in coordinates LOCAL to the wrapper's bbox.
// Outer edge is x=0 (hero's left edge); inner edge is the
// wave chain described by ``spec``, with every x divided by
// ``bboxMaxX`` so the wave's outermost point lands at local
// x = 1 (= the wrapper's right edge).
function buildLeftOrganicLocalPath(spec, bboxMaxX) {
  const xL = (x) => x / bboxMaxX;
  let d = `M 0,0 L ${xL(spec.topX)},0`;
  for (const s of spec.segments) {
    d += ` C ${xL(s.cp1[0])},${s.cp1[1]} ${xL(s.cp2[0])},${s.cp2[1]} ${xL(s.end[0])},${s.end[1]}`;
  }
  // Snap final anchor to ``bottomX`` if the spec's last
  // segment didn't end exactly there — keeps the bottom-edge
  // transition crisp regardless of how the wave terminates.
  if (spec.segments.length && spec.segments[spec.segments.length - 1].end[0] !== spec.bottomX) {
    d += ` L ${xL(spec.bottomX)},1`;
  }
  d += ` L 0,1 Z`;
  return d;
}

// Mirror of ``buildLeftOrganicLocalPath`` — for the
// RIGHT-anchored organic seam in wrapper-local coordinates.
// First we rescale each x to local (divide by ``bboxMaxX``),
// then mirror it across local x=0.5 (``1 - x_local``). The
// result has the same wave shape mirrored, so a two-image
// hero shows the two photos as mirror images of each other
// (identical wobble pattern, opposite handedness).
function buildRightOrganicLocalPath(spec, bboxMaxX) {
  const xR = (x) => 1 - x / bboxMaxX;
  let d = `M 1,0 L ${xR(spec.topX)},0`;
  for (const s of spec.segments) {
    d += ` C ${xR(s.cp1[0])},${s.cp1[1]} ${xR(s.cp2[0])},${s.cp2[1]} ${xR(s.end[0])},${s.end[1]}`;
  }
  if (spec.segments.length && spec.segments[spec.segments.length - 1].end[0] !== spec.bottomX) {
    d += ` L ${xR(spec.bottomX)},1`;
  }
  d += ` L 1,1 Z`;
  return d;
}

// Resolve the photo geometry for a given (mode, image index,
// total). Returns BOTH:
//   * ``wrapperStyle`` — absolute ``top / left / width / height``
//     in % of the hero, sizing the photo wrapper exactly to the
//     visible region's bounding box. Earlier versions made every
//     wrapper full-bleed (``inset: 0``) and let ``clip-path``
//     alone carve the visible region; the consequence was that
//     the inner ``<img>`` (with ``object-fit: cover``) centred
//     itself on the HERO's centre, so the visible curved slice
//     showed the LEFT-most strip of a left-anchored image (which
//     usually crops the subject). Sizing the wrapper to the
//     bbox makes ``object-fit: cover`` centre the image on the
//     bbox's centre, which coincides with the visible region's
//     visual centre — so the visible curve now shows the MIDDLE
//     of the source image.
//   * ``pathD`` — the SVG path's ``d`` attribute in coordinates
//     LOCAL to the wrapper's bbox (0–1 of the bbox, not the
//     hero). The clipPath consumes this directly with
//     ``clipPathUnits="objectBoundingBox"`` for free responsive
//     scaling.
//
// The wrapper's bbox max x in hero coordinates equals:
//   * the smooth siblings' ``CTRL_X`` (the curve reaches its
//     extreme exactly at ``CTRL_X`` by construction — control
//     points share their x with the bulge max, vertical tangent),
//   * the organic sibling's convex-hull max x (which is a safe
//     upper bound on the wave's actual max x — the curve never
//     exits its control polygon's convex hull, so the wrapper
//     definitely contains the visible region).
//
// Same dispatch shape as HeroDiagonalSplit's
// ``resolvePolygonPoints`` so contributors who know one
// renderer find their footing in the other instantly.
function resolvePhotoGeometry({ modeId, imageIndex, imageCount }) {
  const isDeep = modeId === MODE_CIRCLE_DEEP;
  const isOrganic = modeId === MODE_CIRCLE_ORGANIC;
  // Pick the (topX, ctrlX) — or organic (topX, spec) — for the
  // (mode, image-count, image-index) tuple. ``isLeftAnchored``
  // distinguishes the two-image LEFT photo (and the single-image
  // case, always left-anchored) from the two-image RIGHT photo.
  const isLeftAnchored = imageCount === 1 || imageIndex === 0;
  let topX;
  let bboxMaxX;
  let pathD;
  if (isOrganic) {
    const spec = imageCount === 1 ? ORGANIC_SINGLE_SPEC : ORGANIC_TWO_SPEC;
    bboxMaxX = organicMaxX(spec);
    pathD = isLeftAnchored
      ? buildLeftOrganicLocalPath(spec, bboxMaxX)
      : buildRightOrganicLocalPath(spec, bboxMaxX);
    topX = spec.topX; // unused below; assignment for symmetry
  } else if (isDeep) {
    if (imageCount === 1) {
      topX = DEEP_SINGLE_TOP_X;
      bboxMaxX = DEEP_SINGLE_CTRL_X;
    } else {
      topX = DEEP_TWO_TOP_X;
      bboxMaxX = DEEP_TWO_CTRL_X;
    }
    pathD = isLeftAnchored
      ? buildLeftLocalPath(topX, bboxMaxX)
      : buildRightLocalPath(topX, bboxMaxX);
  } else {
    if (imageCount === 1) {
      topX = SUBTLE_SINGLE_TOP_X;
      bboxMaxX = SUBTLE_SINGLE_CTRL_X;
    } else {
      topX = SUBTLE_TWO_TOP_X;
      bboxMaxX = SUBTLE_TWO_CTRL_X;
    }
    pathD = isLeftAnchored
      ? buildLeftLocalPath(topX, bboxMaxX)
      : buildRightLocalPath(topX, bboxMaxX);
  }
  // Wrapper position. LEFT-anchored photos hug the hero's left
  // edge (``left: 0``); RIGHT-anchored photos hug the right
  // edge (``left: (1 - bboxMaxX) * 100%``). Both span the full
  // hero height.
  const wrapperStyle = isLeftAnchored
    ? {
        top: "0%",
        left: "0%",
        width: `${bboxMaxX * 100}%`,
        height: "100%",
      }
    : {
        top: "0%",
        left: `${(1 - bboxMaxX) * 100}%`,
        width: `${bboxMaxX * 100}%`,
        height: "100%",
      };
  return { wrapperStyle, pathD };
}

// ---------------------------------------------------------------------------
// Text column inset — wedges text immediately next to the photo bulge(s)
// ---------------------------------------------------------------------------
//
// The text column is rendered on top of the photo layer. To prevent
// heading / body / CTA from occupying the same horizontal space as
// the visible curve, we apply an asymmetric inline padding that
// reserves the photo's WIDEST horizontal extent on each photo-side.
// Same approach the Diagonal Split family ships in
// ``HeroDiagonalSplit.resolveTextInset``: each side reserves THAT
// SIDE's photo extent (the per-side widest visible edge), not the
// worst-case across both — so the text wedges directly next to
// each photo rather than recessing into a smaller central rectangle.
//
// IMPORTANT — use the actual CURVE MAX, not the wrapper bbox.
// The wrapper's bbox extends to ``bboxMaxX`` (= ``ctrlX``) in
// hero coords, but the visible bezier curve only reaches
// ``smoothCurveMaxX(topX, bboxMaxX)`` at its t=0.5 vertical
// tangent (see the helper above for the derivation — the
// difference between bbox edge and curve max is ``0.25·(bboxMaxX
// - topX)``, ranging from ~2% in the subtle two-image case to
// ~9% in the deep single-image case). Using the bbox edge as the
// inset would push the text column past the wrapper edge —
// leaving a visible empty band between the photo's actual curved
// edge and the text. Using the curve max parks the text right
// next to the visible photo edge, separated only by the
// breakpoint's ``contentPx`` cosmetic gap — the "text wedged
// next to the photo" reading the family is named for.
//
// For the organic sibling we keep the convex-hull bound
// (``organicMaxX``) — the wave path's bulge max varies
// segment-to-segment and the convex hull is the safest tight
// bound. The over-reservation vs the actual wave max stays
// under ~1-2% per side because the spec's control points are
// chosen close to the seam's anchor x's.

function resolveTextInset({ modeId, imageCount, contentPx }) {
  const isDeep = modeId === MODE_CIRCLE_DEEP;
  const isOrganic = modeId === MODE_CIRCLE_ORGANIC;
  if (imageCount === 0) {
    return { paddingLeft: contentPx, paddingRight: contentPx };
  }
  if (imageCount === 1) {
    // Single image always anchors LEFT; the photo's widest
    // visible extent in hero coords is the curve max for smooth
    // siblings, the convex-hull bound for organic. Reserve that
    // as the LEFT padding; the RIGHT side just gets the cosmetic
    // breathing room so the text column hugs the hero's right
    // outer edge — that's what makes the text feel "next to" the
    // photo rather than centered in the remaining gap.
    let edgePct;
    if (isOrganic) {
      edgePct = organicMaxX(ORGANIC_SINGLE_SPEC) * 100;
    } else if (isDeep) {
      edgePct = smoothCurveMaxX(DEEP_SINGLE_TOP_X, DEEP_SINGLE_CTRL_X) * 100;
    } else {
      edgePct = smoothCurveMaxX(SUBTLE_SINGLE_TOP_X, SUBTLE_SINGLE_CTRL_X) * 100;
    }
    return {
      paddingLeft: `calc(${edgePct}% + ${contentPx}px)`,
      paddingRight: `${contentPx}px`,
    };
  }
  // Two images: bulges from both sides reach ``edgePct`` (left
  // photo) and ``1 - edgePct`` (right photo, mirrored). Both
  // photos use the same tuning constants by construction so the
  // edge values are numerically identical on each side; we
  // compute it once and apply symmetrically.
  //
  // The text column ends up CONSTANT-WIDTH (no narrow midline
  // pinch like the earlier mirrored geometry would have), and
  // each side's text edge sits one ``contentPx`` away from
  // the corresponding photo's actual visible curved edge.
  let edgePct;
  if (isOrganic) {
    edgePct = organicMaxX(ORGANIC_TWO_SPEC) * 100;
  } else if (isDeep) {
    edgePct = smoothCurveMaxX(DEEP_TWO_TOP_X, DEEP_TWO_CTRL_X) * 100;
  } else {
    edgePct = smoothCurveMaxX(SUBTLE_TWO_TOP_X, SUBTLE_TWO_CTRL_X) * 100;
  }
  const inset = `calc(${edgePct}% + ${contentPx}px)`;
  return { paddingLeft: inset, paddingRight: inset };
}

function addCssLength(baseValue, extraValue) {
  if (!extraValue) return baseValue;
  if (!baseValue) return extraValue;
  const base = typeof baseValue === "number" ? `${baseValue}px` : baseValue;
  return `calc(${base} + ${extraValue})`;
}

function combineHorizontalInsets(slotInset, contentInsetStyle) {
  const {
    paddingLeft: pagePaddingLeft,
    paddingRight: pagePaddingRight,
    ...restContentInset
  } = contentInsetStyle || {};

  return {
    ...restContentInset,
    paddingLeft: addCssLength(slotInset.paddingLeft, pagePaddingLeft),
    paddingRight: addCssLength(slotInset.paddingRight, pagePaddingRight),
  };
}

function slotVerticalProgress(slotName) {
  if (slotName === "heading") return 0.2;
  if (slotName === "cta") return 0.8;
  return 0.5;
}

function smoothCurveXAtProgress(topX, bboxMaxX, progress) {
  const inverseProgress = 1 - progress;
  return (
    (inverseProgress ** 3) * topX +
    3 * (inverseProgress ** 2) * progress * bboxMaxX +
    3 * inverseProgress * (progress ** 2) * bboxMaxX +
    (progress ** 3) * topX
  );
}

function resolveSmoothSlotInset({ modeId, imageCount, contentPx, slotName }) {
  const isDeep = modeId === MODE_CIRCLE_DEEP;
  const topX = imageCount === 1
    ? (isDeep ? DEEP_SINGLE_TOP_X : SUBTLE_SINGLE_TOP_X)
    : (isDeep ? DEEP_TWO_TOP_X : SUBTLE_TWO_TOP_X);
  const bboxMaxX = imageCount === 1
    ? (isDeep ? DEEP_SINGLE_CTRL_X : SUBTLE_SINGLE_CTRL_X)
    : (isDeep ? DEEP_TWO_CTRL_X : SUBTLE_TWO_CTRL_X);
  const edgePct = smoothCurveXAtProgress(topX, bboxMaxX, slotVerticalProgress(slotName)) * 100;
  const inset = `calc(${edgePct}% + ${contentPx}px)`;

  if (imageCount === 1) {
    return { paddingLeft: inset, paddingRight: `${contentPx}px` };
  }

  return { paddingLeft: inset, paddingRight: inset };
}

function resolveSlotTextInset({ modeId, imageCount, contentPx, slotName }) {
  if (modeId === MODE_CIRCLE_ORGANIC || imageCount === 0) {
    return resolveTextInset({ modeId, imageCount, contentPx });
  }

  return resolveSmoothSlotInset({ modeId, imageCount, contentPx, slotName });
}

// ---------------------------------------------------------------------------
// HeroCircleSplit main renderer
// ---------------------------------------------------------------------------

const HeroCircleSplit = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const gallerySlotData = heroSlots?.getSlot("gallery");
  const galleryItems = useMemo(() => {
    if (!gallerySlotData) return [];
    if (Array.isArray(gallerySlotData)) return gallerySlotData;
    return [gallerySlotData];
  }, [gallerySlotData]);
  const images = useMemo(
    () => galleryItems.filter((it) => it?.type === "image").slice(0, MAX_IMAGES),
    [galleryItems],
  );
  const imageCount = images.length;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  const modeId = item?.hero_mode;
  const isDeepVariant = modeId === MODE_CIRCLE_DEEP;
  const isOrganicVariant = modeId === MODE_CIRCLE_ORGANIC;
  // Variant marker class drives any per-sibling CSS hooks
  // (e.g. organic might want a slightly heavier inner shadow
  // to make the irregular edge read more like torn paper).
  // Mutually exclusive — a mode is exactly one of subtle /
  // deep / organic — so we pick the first applicable rather
  // than concatenating.
  const variantClass = isOrganicVariant
    ? "hero-circle-split--organic"
    : isDeepVariant
      ? "hero-circle-split--deep"
      : "hero-circle-split--subtle";

  // Each hero instance gets its own unique base id so the SVG
  // ``<clipPath>`` element ids don't collide when multiple
  // Circle Split heroes mount on the same page (e.g. a
  // multi-page editor preview, a long-scroll demo). ``useId``
  // returns an opaque, render-stable, document-unique string.
  // We append a per-photo suffix (``-primary`` / ``-secondary``)
  // so the two-image case has distinct clipPath ids, and
  // sanitise the hooks-style colon out of the result because
  // ``url(#…)`` references in CSS treat ``:`` as a pseudo-class
  // delimiter on some browsers.
  const reactClipId = useId().replace(/:/g, "");
  const clipIdFor = (suffix) => `hero-circle-split-clip-${reactClipId}-${suffix}`;

  // Vertical break-out for the photo layer. Same purpose as
  // HeroSplit / HeroDiagonalSplit: without this, the photo
  // wrapper sits inside the hero root's padding box and a band
  // of background is visible above/below the curve. Only
  // applied on desktop (the stacked mobile fallback uses a
  // regular rectangle that respects the hero's padding).
  const heroPad = useHeroVerticalPadding(heroBase.heroRootRef);
  const photoBreakoutStyle = !config.isStacked
    ? {
        top: heroPad.top ? `-${heroPad.top}px` : 0,
        bottom: heroPad.bottom ? `-${heroPad.bottom}px` : 0,
      }
    : null;

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  const contentAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });
  const clippedHeroStyle = {
    ...containerStyle,
    ...fullBleedStyle,
    overflow: "hidden",
  };

  const heroBackgroundLayer = (
    <Background
      bgImage={heroBase.bgImage}
      imageStyleClasses={heroBase.imageStyleClasses}
      imageInlineStyles={heroBase.imageInlineStyles}
      colorClass={heroBase.colorClass}
      colorStyle={heroBase.colorStyle}
      jsPatternLayer={heroBase.jsPatternLayer}
    />
  );

  // Empty state mirrors HeroDiagonalSplit / HeroPhotoBanner /
  // HeroPhotoFloat: no curved layer mounts until at least one
  // image is available — pinning the absence here guards
  // against a regression that would render a curve-clipped
  // empty box on first mount.
  if (imageCount === 0) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            ref={contentAnim.ref}
            className={`flex flex-col gap-4 w-full max-w-2xl mx-auto ${contentAnim.className}`}
            style={{ padding: config.contentPx, ...contentAnim.style, ...contentInsetStyle }}
          >
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  // Mobile fallback: same images, but rendered as circular photo
  // medallions stacked around the text — no curved cut. Same mobile-
  // collapse reasoning HeroDiagonalSplit / HeroSplit use: the desktop
  // curve needs width to read cleanly, while a true circle preserves
  // this family's identity on narrow screens. A 2-image hero renders
  // one circle above and one circle below the text.
  if (config.isStacked) {
    const hasSplitMobileTray = imageCount > 1;
    const mobileTrayHeight = config.stackedTrayHeight;
    const mobileCircleSize = Math.max(180, mobileTrayHeight - 40);
    const renderMobileTray = (trayImages, keyPrefix) => (
      <div
        className="hero-circle-split-mobile-tray flex w-full"
        style={{ height: `${mobileTrayHeight}px` }}
      >
        {trayImages.map((img, idx) => {
          const src = img.cropped_image_url || img.image_url;
          const { className: filterClass, style: filterStyle } =
            getHeroImageStyle(img);
          return (
            <div
              key={`${keyPrefix}-${img.id || idx}-${idx}`}
              className="relative hero-circle-split-mobile-photo hero-circle-split-mobile-photo--circle"
              style={{
                width: `${mobileCircleSize}px`,
                height: `${mobileCircleSize}px`,
              }}
            >
              {src ? (
                <ProgressiveHeroImage
                  src={src}
                  alt={img.alt_text || ""}
                  loading={idx === 0 ? "eager" : "lazy"}
                  draggable={false}
                  className={`hero-circle-split-img ${filterClass}`.trim()}
                  style={filterStyle}
                />
              ) : (
                <div className="hero-circle-split-img hero-circle-split-img--empty" />
              )}
              <EditableSlotMediaOverlay slotData={img} heroItem={item} />
            </div>
          );
        })}
      </div>
    );
    const topMobileImages = hasSplitMobileTray ? images.slice(0, 1) : images;
    const bottomMobileImages = hasSplitMobileTray ? images.slice(1, 2) : [];

    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={clippedHeroStyle}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />

        <div
          className={
            responsiveHeight
              ? "relative z-10 flex flex-col w-full"
              : "absolute inset-0 z-10 flex flex-col"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          {renderMobileTray(topMobileImages, "circle-mobile-top")}

          <div
            ref={contentAnim.ref}
            className={`flex flex-col justify-center gap-4 flex-1 min-w-0 ${contentAnim.className}`}
            style={{
              paddingLeft: config.contentPx,
              paddingRight: config.contentPx,
              paddingTop: config.contentPy,
              paddingBottom: config.contentPy,
              ...contentAnim.style,
            }}
          >
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>

          {bottomMobileImages.length > 0
            ? renderMobileTray(bottomMobileImages, "circle-mobile-bottom")
            : null}
        </div>
      </div>
    );
  }

  // Desktop layout — curve-clipped photo overlay + text column
  // on top. The photo layer is absolutely positioned at the
  // hero root level so the curves can extend to the hero's
  // outer bounds; the text column sits above with an
  // asymmetric padding that keeps the text clear of the
  // visible curve region.
  const slotInsetFor = (slotName) => combineHorizontalInsets(
    resolveSlotTextInset({
      modeId,
      imageCount,
      contentPx: config.contentPx,
      slotName,
    }),
    contentInsetStyle,
  );

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={clippedHeroStyle}
    >
      {heroBackgroundLayer}
      <ContainerEffectOverlay data={heroBase.data} />

      {/* Inline SVG carrying the per-photo ``<clipPath>``
          definitions. Hidden visually (zero-size, aria-hidden,
          ``position: absolute`` so it doesn't take layout
          space). Each clipPath is referenced by its photo
          wrapper via ``clip-path: url(#<id>)``. We render this
          inside the hero root rather than at document root so
          the clipPath ids are scoped to this instance — multiple
          Circle Split heroes on the same page each get their
          own ``<defs>`` block with unique ids (via ``useId``).
          ``clipPathUnits="objectBoundingBox"`` makes the path
          coordinates 0–1 of each photo wrapper's bounding box,
          which gives us responsive scaling for free. */}
      <svg
        className="hero-circle-split-clip-defs"
        width="0"
        height="0"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          {images.map((img, idx) => {
            const slotKey =
              imageCount === 2 ? (idx === 0 ? "primary" : "secondary") : "primary";
            const { pathD } = resolvePhotoGeometry({
              modeId,
              imageIndex: idx,
              imageCount,
            });
            return (
              <clipPath
                key={`clip-${idx}`}
                id={clipIdFor(slotKey)}
                clipPathUnits="objectBoundingBox"
              >
                <path d={pathD} />
              </clipPath>
            );
          })}
        </defs>
      </svg>

      {/* Photo layer — one wrapper per image. Each wrapper is
          sized via inline ``top / left / width / height`` to
          its curve's BOUNDING BOX in hero coordinates (see
          ``resolvePhotoGeometry``); ``clip-path: url(#…)``
          then carves the wrapper down to the curved visible
          region using the wrapper's local-coord clipPath def
          above. Sizing the wrapper to the bbox (instead of
          full-bleed) makes ``object-fit: cover`` on the inner
          ``<img>`` centre the image content on the visible
          region's centre, not on the hero — without this the
          left-anchored photo would show the leftmost strip of
          the source image, usually cropping out the subject.
          Wrappers don't overlap (by the geometry — see
          ``DEEP_TWO_CTRL_X`` and the organic spec callouts)
          so the z-index plane is shared safely. */}
      <div
        className={`hero-circle-split-photo-layer ${variantClass}`}
        style={{
          ...(photoBreakoutStyle || {}),
        }}
        aria-hidden="true"
      >
        {images.map((img, idx) => {
          const src = img.cropped_image_url || img.image_url;
          const { className: filterClass, style: filterStyle } =
            getHeroImageStyle(img);
          const slotKey =
            imageCount === 2 ? (idx === 0 ? "primary" : "secondary") : "primary";
          const clipUrl = `url(#${clipIdFor(slotKey)})`;
          const { wrapperStyle } = resolvePhotoGeometry({
            modeId,
            imageIndex: idx,
            imageCount,
          });
          return (
            <div
              key={`circle-${img.id || idx}-${idx}`}
              className={`hero-circle-split-photo hero-circle-split-photo--${slotKey}`}
              style={{
                ...wrapperStyle,
                clipPath: clipUrl,
                WebkitClipPath: clipUrl,
              }}
              data-hero-circle-photo={slotKey}
            >
              {src ? (
                <ProgressiveHeroImage
                  src={src}
                  alt={img.alt_text || ""}
                  loading={idx === 0 ? "eager" : "lazy"}
                  draggable={false}
                  className={`hero-circle-split-img ${filterClass}`.trim()}
                  style={filterStyle}
                />
              ) : (
                <div className="hero-circle-split-img hero-circle-split-img--empty" />
              )}
              <EditableSlotMediaOverlay slotData={img} heroItem={item} />
            </div>
          );
        })}
      </div>
      <SlotCapacityAffordance
        heroSlots={heroSlots}
        heroId={item?.id}
        slotName="gallery"
        currentCount={images.length}
        className="absolute left-1/2 top-6 z-[2] w-full max-w-xs -translate-x-1/2 px-4"
      />

      {/* Text column. ``relative z-10`` lifts it above the
          photo layer (which is ``z-0`` via the CSS rule). The
          asymmetric padding from ``resolveTextInset`` reserves
          the photo bulge's space so heading / body / CTA never
          cross the visible photo edge.

          IMPORTANT: this container is intentionally ``w-full``
          with NO ``max-w-*`` cap. Same rationale as
          HeroDiagonalSplit's text column — the inset padding
          is expressed as PERCENTAGES of the container's width,
          and percentage padding only computes correctly
          against the actual container's containing-block
          width. An earlier version ran ``w-full max-w-3xl
          mx-auto`` which capped the inner box at 768px while
          still computing % padding against the full-bleed
          parent — on hero widths > 1024px the padding sum
          could exceed 768px and force the heading to overflow
          the inner box, visually crossing into the curved
          photo regions on either side. Letting the container
          take the full hero width ties the % padding's
          reference frame to the same box that contains the
          text, so the asymmetric inset reserves the bulge's
          exact horizontal extent and the text content area
          equals the unbulged middle column to the pixel. Line
          length is naturally constrained by the inset itself
          (text usable width = 100% - 2*inset), which scales
          with the photo geometry. */}
      <div
        className={
          responsiveHeight
            ? "relative z-10 flex items-center justify-center w-full"
            : "absolute inset-0 z-10 flex items-center justify-center"
        }
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        <div
          ref={contentAnim.ref}
          className={`flex flex-col gap-4 w-full ${contentAnim.className}`}
          style={{
            paddingTop: config.contentPy,
            paddingBottom: config.contentPy,
            ...contentAnim.style,
          }}
        >
          <SlotRenderer
            slotName="heading"
            heroSlots={heroSlots}
            heroBase={heroBase}
            heroId={item?.id}
            slotIndex={1}
            className="box-border min-w-0 w-full"
            style={slotInsetFor("heading")}
          />
          <SlotRenderer
            slotName="body"
            heroSlots={heroSlots}
            heroBase={heroBase}
            heroId={item?.id}
            slotIndex={2}
            className="box-border min-w-0 w-full"
            style={slotInsetFor("body")}
          />
          <SlotRenderer
            slotName="cta"
            heroSlots={heroSlots}
            heroBase={heroBase}
            heroId={item?.id}
            slotIndex={3}
            className="box-border min-w-0 w-full"
            style={slotInsetFor("cta")}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroCircleSplit);
